<template>
  <div class="header">
    <div class="header-info" :class="{ 'is-status-visible': deviceStore.isSupported }">
      <div v-if="deviceStore.isSupported" class="device-status">
        <div>{{ deviceStore.currentTime }}</div>
        <DeviceStatus />
      </div>
      <div class="logo-table">
        <div class="logo" @dblclick="handleLogoDoubleClick">
          <template v-if="isCashnote">
            <img src="@/assets/images/logo-cashnote.png" :class="{ cashnote: isCashnote }" alt="캐시노트 로고" />
          </template>
          <template v-else>
            <template v-if="locale === LocaleLanguage.Ko">
              <img v-if="isWhiteFont" src="@/assets/images/logo-renew-ko-white.png" alt="UP오더 로고" />
              <img v-else src="@/assets/images/logo-renew-ko.png" alt="UP오더 로고" />
            </template>
            <template v-else>
              <img v-if="isWhiteFont" src="@/assets/images/logo-renew-en-white.png" alt="UP오더 로고" />
              <img v-else src="@/assets/images/logo-renew-en.png" alt="UP오더 로고" />
            </template>
          </template>
        </div>

        <div class="table">
          <div class="label">{{ t('label.table') }}</div>
          <div class="vertical-line"></div>
          <div class="table-name">{{ configStore.serverConfig?.TableName }}</div>
        </div>
      </div>
    </div>

    <div class="header-button">
      <BaseButtonGroup style="height: 100%">
        <BaseButton2 v-if="configStore.nvrAddress" class="header__button" @button-click="handleCCTVClick">
          <template #icon>
            <img src="@/assets/images/icon-cctv-left-nav.svg" alt="CCTV" style="width: 2.5vw" />
          </template>
          CCTV
        </BaseButton2>

        <BaseButton2
          v-if="configStore.config.IsOriginInformationUsed"
          width="auto"
          class="header__button"
          @button-click="handleOriginInfoClick"
        >
          <template #icon>
            <img
              src="@/assets/images/icon-origin-information_black.png"
              alt="원산지 정보 아이콘"
              style="width: 2.5vw"
            />
          </template>
          {{ t('button.origin_information') }}
        </BaseButton2>
      </BaseButtonGroup>
    </div>

    <div class="language">
      <BaseSelectLanguage2 />
    </div>
  </div>
</template>

<script setup lang="ts">
  import * as Android from '@/api/android';
  import BaseButton2 from '@/components/base/BaseButton2.vue';
  import BaseButtonGroup from '@/components/base/BaseButtonGroup.vue';
  import BaseSelectLanguage2 from '@/components/base/BaseSelectLanguage2.vue';
  import DeviceStatus from '@/components/DeviceStatus.vue';
  import { LocaleLanguage } from '@/enums/LocaleLanguage';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useDeviceStore } from '@/stores/device';
  import { useModalStore } from '@/stores/modal';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  import OriginInformationModal from './OriginInformationModal.vue';

  const { t, locale } = useI18n();
  const route = useRoute();
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const deviceStore = useDeviceStore();

  const isCashnote = computed(() => {
    return configStore.config.ManageCompany === 'KPN' || configStore.config.ManageCompany === 'KCD';
  });

  const isWhiteFont = computed(() => {
    switch (configStore.config.Theme) {
      case 'light':
        return false;
      case 'dark-gray':
        return true;
      case 'green':
        return false;
      case 'blue':
        return true;
      case 'light-blue':
        return true;
      case 'red':
        return false;
      default:
        return false;
    }
  });

  /** 로고 더블클릭 */
  const handleLogoDoubleClick = () => {
    if (route.params.StoreID) return;
    modalStore.openAdminAuthModal();
  };

  /** CCTV 버튼 클릭 */
  const handleCCTVClick = () => {
    if (configStore.nvrAddress) {
      commonStore.clearWaitingPopupInterval();
      Android.showCCTV(configStore.nvrAddress, configStore.nvrChannelCount);
    }
  };

  /** 원산지 정보 버튼 클릭 */
  const handleOriginInfoClick = () => modalStore.openFullScreenModal(OriginInformationModal, {});
</script>

<style lang="scss" scoped>
  .header {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 1.1194vw;
    width: 100%;
    height: 100%;
    padding: 0.7813vw 1.9531vw;
    align-items: center;
    border-bottom: 1px solid var(--header-shadow);
    box-shadow: 0px 0.2344vw 0.4688vw #a5a5a529;
    background-color: var(--header-bg);
    color: var(--header-text);

    .header-info {
      display: grid;
      grid-template-rows: auto auto;

      &.is-status-visible {
        grid-template-rows: 1fr;
      }
    }

    .device-status {
      display: flex;
      gap: 1.1719vw;
      font-size: 1.0938vw;
    }

    .logo-table {
      display: grid;
      grid-template-columns: 11.7188vw auto;
    }

    .logo {
      display: grid;
      align-items: center;
      height: 100%;
      img {
        width: 6.6406vw;

        &.cashnote {
          width: 3.9063vw;
        }
      }
    }

    .table {
      display: grid;
      grid-template-columns: auto auto 1fr;
      align-items: center;
      font-size: 1.875vw;
      font-weight: bold;
      .label {
        color: var(--header-text-secondary);
      }
      .vertical-line {
        height: 1.5625vw;
        margin: 0 0.7813vw;
        border-left: 1px solid var(--header-vertical-border);
        color: var(--header-text);
      }
      .table-name {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        min-width: 0; /* 👈 이게 중요함! */
      }
    }

    .header__button {
      height: 100%;
    }

    .language {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      cursor: pointer;

      @media (max-width: 768px) {
        width: 3.125vw;
        height: 3.125vw;
      }
    }
  }
</style>
